/**
 * Main-thread client for the diff worker. Exposes an async `IAsyncDiffComputer`.
 *
 * Inputs travel as `string[]` via structured-clone postMessage. The response
 * is a small JSON-friendly `WireLinesDiff`; the main thread reconstructs the
 * full `LinesDiff` and runs `_adaptLinesDiff` locally (it already has the
 * input strings).
 */

import { DiffOptions, DiffResult, IDiffComputer } from '../api/diffResult.js';
import { _adaptLinesDiff } from '../api/createDiffComputer.js';
import { wireToLinesDiff } from './wire.js';
import type {
    Backend,
    ComputeRequest,
    ComputeResponseOk,
    InitRequest,
    WorkerResponse,
} from './protocol.js';

export interface IAsyncDiffComputer {
    computeDiff(original: string, modified: string, options?: DiffOptions): Promise<DiffResult>;
    dispose(): void;
}

export interface CreateWorkerDiffComputerOptions {
    /** Use the WASM backend inside the worker. Default: false (TS). */
    useWasm?: boolean;
    /**
     * Override worker construction. Defaults to
     * `new Worker(new URL('./worker.ts?esm', import.meta.url), { type: 'module' })`.
     *
     * The `?esm` marker is recognized by `@vscode/rollup-plugin-esm-url`,
     * `@vscode/esbuild-plugin-esm-url`, and `@vscode/esm-url-webpack-plugin`,
     * which auto-discover sibling worker entries when bundling consumer apps.
     * After bundling, the URL resolves to a sibling `worker.js`. Override this
     * only when your toolchain needs a non-default discovery mechanism.
     */
    workerFactory?: () => Worker;
}

export async function createWorkerDiffComputer(
    opts: CreateWorkerDiffComputerOptions = {},
): Promise<IAsyncDiffComputer> {
    const worker = opts.workerFactory ? opts.workerFactory() : defaultWorkerFactory();
    const backend: Backend = opts.useWasm ? 'wasm' : 'ts';
    const computer = new _WorkerDiffComputer(worker, backend);
    await computer.init();
    return computer;
}

function defaultWorkerFactory(): Worker {
    // The `?esm` marker is auto-discovered by `@vscode/*-plugin-esm-url`
    // bundler plugins; they emit `worker.ts` as a sibling `worker.js` bundle
    // and rewrite this URL accordingly.
    return new Worker(new URL('./worker.ts?esm', import.meta.url), { type: 'module' });
}

interface Pending {
    resolve: (value: DiffResult) => void;
    reject: (err: Error) => void;
    original: string;
    modified: string;
    originalLines: string[];
    modifiedLines: string[];
}

interface PendingInit {
    resolve: () => void;
    reject: (err: Error) => void;
}

class _WorkerDiffComputer implements IAsyncDiffComputer {
    private _nextId = 1;
    private readonly _pending = new Map<number, Pending>();
    private _pendingInit: PendingInit | null = null;
    private _disposed = false;

    constructor(
        private readonly _worker: Worker,
        private readonly _backend: Backend,
    ) {
        this._worker.addEventListener('message', (ev: MessageEvent<WorkerResponse>) => {
            this._onMessage(ev.data);
        });
        this._worker.addEventListener('error', (ev) => {
            this._failAll(new Error(`Worker error: ${ev.message ?? 'unknown'}`));
        });
    }

    init(): Promise<void> {
        const id = this._nextId++;
        const req: InitRequest = { kind: 'init', id, backend: this._backend };
        return new Promise<void>((resolve, reject) => {
            this._pendingInit = { resolve, reject };
            this._worker.postMessage(req);
        });
    }

    computeDiff(original: string, modified: string, options: DiffOptions = {}): Promise<DiffResult> {
        if (this._disposed) {
            return Promise.reject(new Error('Worker computer disposed'));
        }
        const id = this._nextId++;
        const originalLines = original.split('\n');
        const modifiedLines = modified.split('\n');

        const msg: ComputeRequest = {
            kind: 'computeDiff',
            id,
            backend: this._backend,
            originalLines,
            modifiedLines,
            options,
        };
        return new Promise<DiffResult>((resolve, reject) => {
            this._pending.set(id, {
                resolve,
                reject,
                original,
                modified,
                originalLines,
                modifiedLines,
            });
            this._worker.postMessage(msg);
        });
    }

    dispose(): void {
        if (this._disposed) return;
        this._disposed = true;
        this._failAll(new Error('Worker computer disposed'));
        this._worker.terminate();
    }

    private _onMessage(msg: WorkerResponse): void {
        if (msg.kind === 'init') {
            const pending = this._pendingInit;
            this._pendingInit = null;
            pending?.resolve();
            return;
        }
        const entry = this._pending.get(msg.id);
        if (!entry) {
            // Init failure is reported on the init id with `kind === 'computeDiff'`
            // and `ok === false`; route to the init promise if it's still pending.
            if (!msg.ok && this._pendingInit) {
                const p = this._pendingInit;
                this._pendingInit = null;
                p.reject(new Error(msg.error));
            }
            return;
        }
        this._pending.delete(msg.id);
        if (!msg.ok) {
            entry.reject(new Error(msg.error));
            return;
        }
        try {
            const linesDiff = wireToLinesDiff((msg as ComputeResponseOk).result);
            const result = _adaptLinesDiff(
                linesDiff,
                entry.original,
                entry.modified,
                entry.originalLines,
                entry.modifiedLines,
            );
            entry.resolve(result);
        } catch (err) {
            entry.reject(err instanceof Error ? err : new Error(String(err)));
        }
    }

    private _failAll(err: Error): void {
        for (const p of this._pending.values()) p.reject(err);
        this._pending.clear();
        if (this._pendingInit) {
            this._pendingInit.reject(err);
            this._pendingInit = null;
        }
    }
}

/** Wrap an async computer to satisfy `IDiffComputer` shape — only useful if
 * the caller can `await` synchronously, otherwise prefer `IAsyncDiffComputer`. */
export type AsyncDiffComputer = IAsyncDiffComputer;
export type SyncDiffComputer = IDiffComputer;
